import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { api } from "../../../../api/apiClient";
import config from "../../../../config";

import AddIcon from "@mui/icons-material/Add";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeIcon from "@mui/icons-material/Badge";
import BlockIcon from "@mui/icons-material/Block";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventIcon from "@mui/icons-material/Event";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import SearchIcon from "@mui/icons-material/Search";

import PageHeader from "../../../../app/layout/PageHeader";
import ServiceForm from "./CreateServiceForm";
import { ServiceDto } from "../../../types/types";
import { useAccess } from "../../../../context/AccessContext";
import { useAuth } from "../../../../context/AuthContext";
import { FEATURE_CODES, FeatureCode } from "../../../../constants/constants";
import "../../../../css/buttons.css";
import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/tables.css";
import "./ServiceManagementTab.css";

interface ServiceManagementTabProps {
  tenantId: number;
  initialServiceId?: number | null;
  onManageInstances: (serviceId: number) => void;
  onViewCalendar: (serviceId: number) => void;
  onManageMembers: (serviceId: number) => void;
  onManageRota: (serviceId: number) => void;
}

interface ServiceAction {
  title: string;
  description: string;
  icon: React.ReactNode;
  tone: "green" | "purple" | "blue" | "orange" | "slate";
  onClick: () => void;
  feature: FeatureCode;
}

export default function ServiceManagementTab({
  tenantId,
  initialServiceId,
  onManageInstances,
  onViewCalendar,
  onManageMembers,
  onManageRota,
}: ServiceManagementTabProps) {
  const history = useHistory();
  const { hasFeature } = useAccess();
  const { isTenantAdmin, isSystemAdmin } = useAuth();
  const canManageServiceLifecycle = isTenantAdmin || isSystemAdmin;
  const [services, setServices] = useState<ServiceDto[]>([]);
  const [search, setSearch] = useState("");
  const [selectedService, setSelectedService] = useState<ServiceDto | null>(null);
  const [editingService, setEditingService] = useState<ServiceDto | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formServiceName, setFormServiceName] = useState("");

  const refresh = useCallback(() => {
    api<ServiceDto[]>(`/api/services?tenantId=${tenantId}`).then((result) => {
      setServices(result);
      setSelectedService((current) => {
        const serviceId = current?.serviceId ?? initialServiceId;
        return serviceId
          ? result.find((service) => service.serviceId === serviceId) ?? null
          : null;
      });
    });
  }, [tenantId, initialServiceId]);

  useEffect(() => {
    if (tenantId) {
      refresh();
    }
  }, [tenantId, refresh]);

  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingService(null);
    setFormServiceName("");
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return services;

    return services.filter((s) => s.name.toLowerCase().includes(term));
  }, [services, search]);

  const isEditing = Boolean(showCreateForm || editingService);
  const pageTitle = editingService
    ? `Editing — ${formServiceName || editingService.name}`
    : showCreateForm
      ? formServiceName
        ? `Creating — ${formServiceName}`
        : "Creating Service"
      : selectedService
        ? selectedService.name
        : "Service Management";

  const openCreate = () => {
    setSelectedService(null);
    setEditingService(null);
    setFormServiceName("");
    setShowCreateForm(true);
  };

  const disableService = async (service: ServiceDto) => {
    await api(`/api/services/${service.serviceId}/disable`, { method: "POST" });
    refresh();
  };

  const renderServiceSummary = (service: ServiceDto) =>
    service.description || "Service";

  const renderServiceLogo = (service: ServiceDto) =>
    service.hasLogo ? (
      <img
        src={`${config.API_BASE_URL}${
          service.logoUrl ?? `/api/services/${service.serviceId}/logo`
        }`}
        alt=""
      />
    ) : (
      <GroupsIcon />
    );

  const selectedActions: ServiceAction[] = selectedService
    ? ([
        {
          title: "Service Setup",
          description: "Manage service setup and details.",
          icon: <EditIcon fontSize="small" />,
          tone: "green",
          onClick: () => {
            setShowCreateForm(false);
            setEditingService(selectedService);
          },
          feature: FEATURE_CODES.SERVICE_DETAILS,
        },
        {
          title: "Team Roles",
          description: "Define roles for your team.",
          icon: <BadgeIcon fontSize="small" />,
          tone: "slate",
          onClick: () => history.push(`/services/${selectedService.serviceId}/roles`),
          feature: FEATURE_CODES.SERVICE_TEAM_ROLES,
        },
        {
          title: "Service Members",
          description: "Manage enrolled service members.",
          icon: <GroupsIcon fontSize="small" />,
          tone: "purple",
          onClick: () => onManageMembers(selectedService.serviceId),
          feature: FEATURE_CODES.SERVICE_MEMBERS,
        },
        {
          title: "Manage Rota",
          description: "Coordinate availability and assignments.",
          icon: <EventAvailableIcon fontSize="small" />,
          tone: "orange",
          onClick: () => onManageRota(selectedService.serviceId),
          feature: FEATURE_CODES.SERVICE_ROTA,
        },
        {
          title: "Manage Sessions",
          description: "Create and update sessions.",
          icon: <EventIcon fontSize="small" />,
          tone: "blue",
          onClick: () => onManageInstances(selectedService.serviceId),
          feature: FEATURE_CODES.SERVICE_SCHEDULE_SESSIONS,
        },
        {
          title: "Manage Attendance",
          description: "Register and check in attendees.",
          icon: <HowToRegIcon fontSize="small" />,
          tone: "purple",
          onClick: () => history.push("/party-service-registration", {
            selectedServiceId: selectedService.serviceId,
            initialTab: "register",
            registrationOrigin: "services",
            originServiceId: selectedService.serviceId,
          }),
          feature: FEATURE_CODES.SERVICE_ATTENDANCE_CHECK_IN,
        },
        {
          title: "Session Reporting",
          description: "Record outcomes and incidents.",
          icon: <AssessmentIcon fontSize="small" />,
          tone: "blue",
          onClick: () => history.push(`/services/${selectedService.serviceId}/session-reports`),
          feature: FEATURE_CODES.SERVICE_SESSION_REPORTS,
        },
        {
          title: "Calendar",
          description: "Review sessions in calendar view.",
          icon: <CalendarMonthIcon fontSize="small" />,
          tone: "blue",
          onClick: () => onViewCalendar(selectedService.serviceId),
          feature: FEATURE_CODES.SERVICE_CALENDAR,
        },
      ] as ServiceAction[])
    : [];

  return (
    <>
      <PageHeader
        title={pageTitle}
        actions={
          <div className="service-management-header-actions">
            {selectedService && !isEditing && (
              <button
                type="button"
                className="icon-button-muted-solid"
                onClick={() => {
                  setSelectedService(null);
                }}
                title="Back to services"
                aria-label="Back to services"
              >
                <ArrowBackIcon fontSize="small" />
              </button>
            )}
            {!selectedService && !isEditing && canManageServiceLifecycle && (
              <>
                <button
                  type="button"
                  className="icon-button-solid icon-button-solid--success"
                  onClick={openCreate}
                  title="Create service"
                  aria-label="Create service"
                >
                  <AddIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="icon-button-muted-solid"
                  onClick={() => history.push("/dashboard")}
                  title="Back to dashboard"
                  aria-label="Back to dashboard"
                >
                  <ArrowBackIcon fontSize="small" />
                </button>
              </>
            )}
            {isEditing && (
              <button
                type="button"
                className="icon-button-solid icon-button-solid--danger"
                onClick={cancelForm}
                title="Cancel"
                aria-label="Cancel"
              >
                <CloseIcon fontSize="small" />
              </button>
            )}
          </div>
        }
      />

      {isEditing && (
        <div className="card">
          <ServiceForm
            tenantId={tenantId}
            mode={editingService ? "edit" : "create"}
            service={editingService ?? undefined}
            onNameChange={setFormServiceName}
            onSaved={() => {
              cancelForm();
              refresh();
            }}
            onCancel={cancelForm}
          />
        </div>
      )}

      {!isEditing && !selectedService && (
        <div className="service-management-list">
          <div className="service-management-toolbar">
            <div className="search-container service-management-search">
              <div className="search-input-wrapper">
                <SearchIcon className="search-icon-left" fontSize="small" />
                <input
                  className="form-input search-input-with-icons"
                  placeholder="Search services..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="search-clear-button"
                    onClick={() => setSearch("")}
                    aria-label="Clear service search"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {filtered.length === 0 && (
            <div className="card">
              <div className="field-hint" style={{ marginBottom: 0 }}>
                No services found.
              </div>
            </div>
          )}

          <div className="service-card-grid">
            {filtered.map((service) => (
              <button
                type="button"
                key={service.serviceId}
                className="service-summary-card"
                style={{ ["--service-color" as string]: service.colorHex }}
                onClick={() => setSelectedService(service)}
                aria-label={`Open ${service.name}`}
              >
                <div className="service-summary-main">
                  <div className="service-summary-icon">
                    {renderServiceLogo(service)}
                  </div>
                  <div className="service-summary-copy">
                    <h3>{service.name}</h3>
                    <p>{renderServiceSummary(service)}</p>
                    <div className="service-summary-meta">
                      <span className="service-status-dot" />
                      <span>{service.isActive ? "Active" : "Inactive"}</span>
                    </div>
                  </div>
                </div>

                <span className="service-summary-rail" aria-hidden="true">
                  <ChevronRightIcon fontSize="small" />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!isEditing && selectedService && (
        <div className="service-detail-shell">
          <section
            className="service-detail-hero"
            style={{ ["--service-color" as string]: selectedService.colorHex }}
          >
            <div className="service-detail-hero-icon">
              {renderServiceLogo(selectedService)}
            </div>
            <div>
              <h2>{selectedService.name}</h2>
              <p>{renderServiceSummary(selectedService)}</p>
            </div>
          </section>

          <section>
            <div className="service-section-heading">Service Actions</div>
            <div className="service-action-list">
              {selectedActions.map((action) => (
                <button
                  key={action.title}
                  type="button"
                  className={`service-action-card service-action-card--${action.tone}`}
                  disabled={!hasFeature(action.feature)}
                  title={
                    hasFeature(action.feature)
                      ? undefined
                      : `${action.title} is not included in your organisation profile`
                  }
                  onClick={action.onClick}
                >
                  <span
                    className={`service-action-icon service-action-icon--${action.tone}`}
                  >
                    {action.icon}
                  </span>
                  <span className="service-action-copy">
                    <strong>{action.title}</strong>
                    <span>{action.description}</span>
                  </span>
                  <span className="service-action-rail" aria-hidden="true">
                    <ChevronRightIcon className="service-action-chevron" />
                  </span>
                </button>
              ))}
            </div>
          </section>

          {canManageServiceLifecycle && <section>
            <div className="service-section-heading">Quick Actions</div>
            <div className="service-action-list service-action-list--compact">
              <button
                type="button"
                className="service-action-card service-action-card--slate"
                onClick={() => disableService(selectedService)}
              >
                <span className="service-action-icon service-action-icon--slate">
                  <BlockIcon fontSize="small" />
                </span>
                <span className="service-action-copy">
                  <strong>Disable Service</strong>
                  <span>Remove this service from active management.</span>
                </span>
                <span className="service-action-rail" aria-hidden="true">
                  <ChevronRightIcon className="service-action-chevron" />
                </span>
              </button>
            </div>
          </section>}
        </div>
      )}
    </>
  );
}
